import {observer} from "mobx-react";
import PageAncestor from "../../core/common/page/pageAncestor";
import {MODULE, PageProps, PERMISSION} from "../../core/metamodel/metaConstant";
import React from "react";
import {EntityNumberGen, EntityNumberGenGroup} from "../domain/entityNumberGenDomain";
import Toolmenu from "../../core/components/toolmenu/toolmenu";
import {ZzFlatButton} from "../../core/components/buttons/buttons";
import entityNumberGenService from "../service/entityNumberGenService";
import {action, ObservableMap} from "mobx";
import {ErrorComp} from "../../core/components/errorComp/errorComp";
import ConfirmationDialog from "../../core/components/confirmationDialog/confirmationDialog";
import EntityNumberGenGroupForm from "../component/entityNumberGenGroupForm";
import mdEntityService from "../../core/metamodel/mdEntityService";
import {FormProps, IDomainObject, lovLabel, preSave, replaceIdWithLov} from "../../core/form/formUtils";
import {DomainAncestor} from "../../core/common/domain/DomainAncestor";
import uiService from "../../core/common/service/uiService";
import {ENTITY_NUMBER_GEN_ENTITY, ENTITY_NUMBER_GEN_GROUP_ENTITY, ENTITY_NUMBER_GEN_LIST} from "../constants";
import gridService from "../../core/grid/gridService";
import Grid, {ClientGrid, GridLocation} from "../../core/grid/domain/grid";
import {MovingBlocks} from "../../core/grid/gridUtils";
import GridComp, {UILOCATION} from "../../core/grid/component/gridComp";
import EntityNumberGenForm, {EntityNumberGenFormDefinition} from "../component/EntityNumberGenForm";
import Form from "../../core/form/form";
import {MdIconButton} from "../../core/components/buttons/icons";
import Add from "material-ui/svg-icons/content/add";
import {valid} from "../../core/form/rules";
import {ValidationMessage} from "../../core/common/domain/commonDomain";

@observer
export default class EntityNumberGenGroupDetail extends PageAncestor<PageProps, undefined> {

  static module = MODULE.invoicing;
  static title = 'Entity number gen group';
  static icon = 'group';
  static protectedPage = true;

  static fetchData(routerParams, query) {
    console.log("EntityNumberGenGroupDetail#fetchData(%O)", query);

    let metadataPromise = mdEntityService.fetchEntityMetadata([ENTITY_NUMBER_GEN_GROUP_ENTITY, ENTITY_NUMBER_GEN_ENTITY], [{entity: 'MdEntity', lov: true}]);
    let gridPromise = gridService.fetchGrids(ENTITY_NUMBER_GEN_LIST);
    let entityGenGroupObjectPromise: Promise<EntityNumberGenGroup>;

    if (routerParams.id === 'new') {
      entityGenGroupObjectPromise = Promise.resolve(new EntityNumberGenGroup({$new: true}));
    } else if (routerParams.id === 'copy') {
      entityGenGroupObjectPromise = entityNumberGenService.readEntityNumberGenGroup(query.copyId)
        .then((entityGenGroupObject: EntityNumberGenGroup) => {
          entityGenGroupObject.cleanOnCopy();
          entityGenGroupObject.entityNumberGenGroupId = undefined;

          entityGenGroupObject.numberGenList.forEach(e => {
            e.group = undefined;
            e.entityNumberGenId = undefined;

            e.$dirty = true;
            e.$new = true;
            e.$added = true;
          });

          return entityGenGroupObject;
        })
    } else {
      entityGenGroupObjectPromise = entityNumberGenService.readEntityNumberGenGroup(routerParams.id);
    }

    return Promise.all([metadataPromise, entityGenGroupObjectPromise, gridPromise])
      .then(([entityMap, entityGenGroupObject, gridLocationMap]: [any, EntityNumberGenGroup, ObservableMap<GridLocation>]) => {
        entityGenGroupObject.$new = !entityGenGroupObject.entityNumberGenGroupId;

        entityGenGroupObject.$movingBlocks = new MovingBlocks(null, false, null,
          new ClientGrid<EntityNumberGen>(gridLocationMap.get(ENTITY_NUMBER_GEN_LIST), "numberGenList", entityGenGroupObject.entityNumberGenGroupId)
        );

        if (routerParams.id === 'copy')
          entityGenGroupObject.$movingBlocks.getBlock(ENTITY_NUMBER_GEN_LIST).$dirty = true;

        entityNumberGenService.entityGenGroupObject = entityGenGroupObject.initAfterRead(routerParams.id === 'new');
      });

  }

  private confirmationDialogRef: ConfirmationDialog;

  componentDidUpdate(prevProps: PageProps) {
    if (prevProps.params.id !== this.props.params.id)
      entityNumberGenService.entityGenGroupObject.$movingBlocks.searchExposedGrids(entityNumberGenService.entityGenGroupObject);
  }

  componentDidMount() {
    entityNumberGenService.entityGenGroupObject.$movingBlocks.searchExposedGrids(entityNumberGenService.entityGenGroupObject);
  }

  componentWillUnmount() {
    entityNumberGenService.entityGenGroupObject = null;
  }

  findRootObject(): DomainAncestor {
    return entityNumberGenService.entityGenGroupObject;
  }

  customValidate = (genGroupObject: EntityNumberGenGroup) => {
    let errors: ValidationMessage[] = [];

    const numberGenGrid: Grid = entityNumberGenService.entityGenGroupObject.$movingBlocks.getBlock(ENTITY_NUMBER_GEN_LIST) as ClientGrid<EntityNumberGen>;

    let editedRows: EntityNumberGen[] = numberGenGrid.getEditedRows().filter(e => !e.getId()) as EntityNumberGen[];

    if (editedRows.length > 1) {
      let hasError: boolean = false;
      for (let i = 0; i < editedRows.length; i++) {
        let noGen = editedRows[i];
        const firstGenNo: string = `${noGen.prefixConstant}${"0".repeat(noGen.padding)}`;

        for (let j = (i + 1); j < editedRows.length; j++) {
          let nextNoGen = editedRows[j];

          if (noGen.prefixConstant === nextNoGen.prefixConstant) {
            errors.push({message: "There are number generation configurations with same prefix constant"});
            hasError = true;
            break;
          }

          if (firstGenNo.startsWith(nextNoGen.prefixConstant) && noGen.getNumberLength() === (nextNoGen.getNumberLength())) {
            errors.push({message: "There are number generation configurations that can generate same numbers"});
            hasError = true;
            break;
          }
        }
        if (hasError)
          break;

      }
    }

    return errors;
  };

  @action onSave = (evt) => {
    console.log('onSave entityNumberGenGroupId = %s, $new = %s', entityNumberGenService.entityGenGroupObject.entityNumberGenGroupId, entityNumberGenService.entityGenGroupObject.$new);
    uiService.showToolbarActionFlag();

    let result = preSave(entityNumberGenService.entityGenGroupObject, this.customValidate, true);
    if (!result) {
      return;
    }

    entityNumberGenService.saveGenGroup(entityNumberGenService.entityGenGroupObject.createDataObjectDto())
      .then(
        action("entityGenGroupSave", (entityGenGroupJson: IDomainObject) => {
            const wasNew = entityNumberGenService.entityGenGroupObject.cleanAfterSave(entityGenGroupJson);

            if (wasNew)
              this.context.router.replaceWith('entityNumberGenGroupDetail', {id: entityNumberGenService.entityGenGroupObject.entityNumberGenGroupId});

            uiService.hideToolbarActionFlag();
          }
        ),
        (error) => {
          entityNumberGenService.entityGenGroupObject.$errors = error.getMessages();
        }
      );

  };

  @action onAdd = (evt) => {
    console.log('onAdd');
    this.context.router.transitionTo("entityNumberGenGroupDetail", {id: 'new'});
  };

  @action onCopy = (evt) => {
    console.log('onCopy');
    this.context.router.transitionTo("entityNumberGenGroupDetail", {id: 'copy'}, {copyId: entityNumberGenService.entityGenGroupObject.entityNumberGenGroupId});
  };

  @action onDelete = (evt) => {
    console.log('onDelete');

    new Promise((resolve, reject) => {
      this.confirmationDialogRef.open(resolve, reject, 'Do you really want to delete this group with its number gens?');
    }).then(() => {

      uiService.showToolbarActionFlag();

      entityNumberGenService.deleteGenGroup(entityNumberGenService.entityGenGroupObject.entityNumberGenGroupId)
        .then(_ => {
          this.goBackOrHome();

          uiService.hideToolbarActionFlag();
        }, error => {
          entityNumberGenService.entityGenGroupObject.$errors = error.getMessages();
        });

    }).catch((e) => {
    })
  };

  @action copyEntityNumberGen = (entityGenGroup: EntityNumberGen): IDomainObject => {
    entityGenGroup.entityNumberGenId = undefined;
    entityGenGroup.dateCreated = undefined;
    entityGenGroup.dateUpdated = undefined;
    entityGenGroup.userCreated = undefined;
    entityGenGroup.tenantId = undefined;

    return entityGenGroup;
  };

  prepareNewNumberGenDefaults(): { padding?: number, prefixConstant?: string, validFrom?: Date, validTo?: Date } {
    if(!entityNumberGenService.entityGenGroupObject.numberGenList || entityNumberGenService.entityGenGroupObject.numberGenList.length === 0)
      return {};

    const lastGen: EntityNumberGen = entityNumberGenService.entityGenGroupObject.numberGenList
      .reduce((prev, current) => (prev.entityNumberGenId > current.entityNumberGenId) ? prev : current);

    const nextYear: number = new Date().getFullYear() + 1;
    let foundNos: string[] = lastGen.prefixConstant.match(/\d+/g);

    let prefixConstant: string = lastGen.prefixConstant;
    let validFrom: Date = new Date(lastGen.validFrom);
    if (validFrom) validFrom.setFullYear(nextYear)
    let validTo: Date = lastGen.validTo ? new Date(lastGen.validTo) : null;
    if (validTo) validTo.setFullYear(nextYear)

    if (foundNos) {
      let noFromPrefix: string = foundNos[foundNos.length - 1];

      prefixConstant = prefixConstant.replace(noFromPrefix, (parseInt(noFromPrefix) + 1).toString());
    }

    return {
      padding: lastGen.padding,
      prefixConstant,
      validFrom,
      validTo
    }
  }

  @action addEntityNumberGen = () => {
    console.log('addEntityNumberGen');

    let grid: Grid = entityNumberGenService.entityGenGroupObject.$movingBlocks.getBlock(ENTITY_NUMBER_GEN_LIST) as ClientGrid<EntityNumberGen>;

    let rowObject = new EntityNumberGen({
      group: entityNumberGenService.entityGenGroupObject.createLov(),
      active: true,
      ...this.prepareNewNumberGenDefaults(),

      $new: true,
      $dirty: true,
      $added: true,
    }).initDefaultValues();

    grid.addRow(rowObject);
    let form: Form<EntityNumberGen> = rowObject.getForm(EntityNumberGenFormDefinition.formName);
    form.$open = true;

    entityNumberGenService.entityGenGroupObject.$dirty = true;
  };

  disableDelete = (numberGen: EntityNumberGen): boolean => {
    return !!numberGen.entityNumberGenId;
  }

  private _createToolMenu(entityGenGroupObject: EntityNumberGenGroup, gridLocation: string) {

    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1}>

        <ZzFlatButton onClick={this.onSave} disabled={!entityGenGroupObject.$dirty} label="Save"
                      permission={entityGenGroupObject.$new ? PERMISSION.New : PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onAdd} disabled={entityGenGroupObject.$new} label="New"
                      permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onCopy} disabled={entityGenGroupObject.$new} label="Copy"
                      permission={PERMISSION.New}/>
        {(!entityGenGroupObject.numberGenList || entityGenGroupObject.numberGenList.length === 0) ?
          <ZzFlatButton onClick={this.onDelete} disabled={entityGenGroupObject.$new} label="Delete"
                        permission={PERMISSION.Delete}/> : null}
      </Toolmenu>
    );
  }

  confirmationDialogRefHandler = comp => this.confirmationDialogRef = comp;

  render() {
    let {params, query} = this.props;

    let entityGenGroupObject: EntityNumberGenGroup = entityNumberGenService.entityGenGroupObject;

    const propsForForm: FormProps<EntityNumberGenGroup, EntityNumberGenGroup> = {
      dataObject: entityGenGroupObject,
      rootObject: entityGenGroupObject,
      entity: mdEntityService.getEntity(ENTITY_NUMBER_GEN_GROUP_ENTITY),
      entities: mdEntityService.entityMap
    };

    const _renderEntityNumberGenGroupForm: JSX.Element = (
      <EntityNumberGenGroupForm {...propsForForm} />
    );

    const _rowEntityNumberGenForm = (
      <EntityNumberGenForm rootObject={entityGenGroupObject}
                           entity={mdEntityService.getEntity(ENTITY_NUMBER_GEN_ENTITY)}
                           entities={mdEntityService.entityMap}
                           dataObject={null}/>
    );

    return (
      <main className="main-content" style={{display: 'flex', flexDirection: 'column'}}>
        {this._createToolMenu(entityGenGroupObject, query.gridLocation)}
        <ErrorComp messages={entityGenGroupObject.$errors}/>
        <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
        <ConfirmationDialog okButtonText="Yes" cancelButtonText="No"
                            ref={this.confirmationDialogRefHandler}/>
        {_renderEntityNumberGenGroupForm}
        <GridComp ref={ENTITY_NUMBER_GEN_LIST}
                  grid={entityGenGroupObject.$movingBlocks.getBlock(ENTITY_NUMBER_GEN_LIST) as Grid}
                  uiLocation={UILOCATION.main}
                  entities={mdEntityService.entityMap}
                  rootObject={entityGenGroupObject}
                  editForm={_rowEntityNumberGenForm} editFormName={EntityNumberGenFormDefinition.formName}
                  showEdit={true}
                  deleteRowIcon="fa fa-trash"
                  multiSelect={false}
                  showDelete={true}
                  disableDeleteFn={this.disableDelete}
                  multiUpdateSaveFn={this.onSave}
                  copyRowObjectFn={this.copyEntityNumberGen}
        >
          <MdIconButton onToolbar onClick={this.addEntityNumberGen}>
            <Add/>
          </MdIconButton>
        </GridComp>
      </main>
    );
  }
}



// WEBPACK FOOTER //
// ./src/invoice/page/entityNumberGenGroupDetail.tsx